/**
 * @fileoverview Audit Log Page Component
 * 
 * Displays audit trail of data modifications across the application. Provides users with
 * a comprehensive view of what data was modified, when, and by whom. This component integrates
 * with jQuery DataTables for advanced table rendering with sorting, pagination, and search
 * capabilities.
 * 
 * @module components/pages/auditlog
 * @requires React - UI framework with hooks for state management
 * @requires react-select - Dropdown select component for table selection
 * @requires jquery - JavaScript library for DOM manipulation
 * @requires datatables.net - Advanced table plugin with sorting/pagination/search
 * @requires config_ip_details - API server URL configuration
 * @requires server_utils - apiCall wrapper function for HTTP requests
 * @requires pageloader - Full-page loading spinner component
 * @requires customicon - Icon rendering component (lucide-react wrapper)
 * @requires common_functions - Utility functions like getUsername
 * @requires config_mapping - Configuration constants including API response codes
 * @requires react-dom - DOM rendering utilities
 * 
 * @component
 * @example
 * // In App.js routing configuration
 * { path: '/audit-log', name: 'Audit Log', element: <AuditLog />, isPrivate: true }
 * 
 * Features:
 * - Dynamic table selection from multiple audit tables
 * - Real-time search filtering across all displayed columns
 * - jQuery DataTables with pagination (5, 10, 50, 100, 500, All rows per page)
 * - Sorting by clicking column headers
 * - Responsive horizontal scrolling for wide tables
 * - Data refresh button to reload audit log
 * - Full-page loading spinner during data fetch
 */

import React, { useState, useEffect, useRef } from 'react';
import Select from "react-select";
import $ from 'jquery';
import "../../../node_modules/datatables.net-dt/css/dataTables.dataTables.css";
import 'datatables.net';
import { API_SERVER_URL } from "../../utils/config_ip_details";
import { apiCall } from "../../utils/server_utils";
import PageLoader from '../common/pageloader';
import CommonIcon from '../common/customicon';
import { getUsername } from "../../utils/common_functions";
import { API_SUCCESS_RESPONSE_CODE } from '../../utils/config_mapping';
import * as ReactDOM from 'react-dom';
import HeaderLogo  from "../structure/headerlogo";

/**
 * AuditLog Component - Audit trail data visualization
 * 
 * Fetches audit log data from the server and displays it in a jQuery DataTable
 * with interactive search, sorting, and pagination features. The component
 * dynamically determines which columns to display based on the API response
 * structure. Multiple tables may be available; the first table is selected by default.
 * 
 * @component
 * @returns {React.ReactElement} Audit log page with dynamic DataTable
 * 
 * State Management:
 * - loading: {boolean} Loading state during API fetch (displays PageLoader spinner)
 * - selectedTable: {Object|null} Currently selected table configuration
 *   @property {string} tableName - Name of the audit log table (e.g., "Channels")
 *   @property {Array<string>} columnsNameList - List of column names to display
 *   @property {Array<Object>} columnsType - Type definitions for each column (select/text/etc.)
 * - tableContent: {Array<Object>} Raw data rows fetched from server
 * - filteredData: {Array<Object>} Data rows after search filtering (currently unused - filtering done via DataTables)
 * - searchText: {string} Current search query - updates DataTable in real-time
 * - tableRef: {useRef} Reference to DOM table element (prepared for future direct access)
 * 
 * API Integration:
 * Endpoint: GET /reference/auditlog
 * Purpose: Retrieve audit log data with column definitions and data types
 * Response Structure:
 * {
 *   "code": 200,
 *   "data": [
 *     {
 *       "tableName": "Channels",
 *       "columnsNameList": ["Category", "ParentCategory", "CategoryType", "Synonyms", "Ignore"],
 *       "columnsType": [
 *         {
 *           "Category": {
 *             "type": "select",
 *             "defaultValues": ["2-fer Knit Tops", "Ankle Sock", ...]
 *           }
 *         }
 *       ]
 *     }
 *   ]
 * }
 * 
 * DataTables Configuration Details:
 * - destroy: true - Destroys previous DataTable instance before creating new one
 *   (necessary when reinitialization happens due to search/data changes)
 * - dom: '<"top"f>rt<"bottom"ilp><"clear">' - Defines layout:
 *   * f = search box at top
 *   * r = table content
 *   * i = table info (e.g., "Showing 1 to 10 of 50")
 *   * l = length menu (rows per page selector)
 *   * p = pagination controls at bottom
 * - scrollX: true - Enable horizontal scrolling for columns that exceed container width
 * - aLengthMenu: [[5,10,50,100,500,-1], [5,10,50,100,500,"All"]] - Page size options
 * - pageLength: 50 - Default page size (50 rows per page)
 * - data: Filtered table rows (filtered by search text across all columns)
 * - columns: Dynamic column definitions from API response columnsNameList
 * - order: [] - No automatic sorting (user clicks headers to sort)
 * 
 * Component Lifecycle:
 * 1. Mount → First useEffect calls fetchData() to load audit log
 * 2. Data loaded → selectedTable and tableContent state updated
 * 3. selectedTable or searchText changes → Second useEffect recreates DataTable with new data/filters
 * 4. Unmount → DataTable destroyed to prevent memory leaks and jQuery plugin conflicts
 * 
 * Search Algorithm:
 * Performs case-insensitive filtering across all selected columns:
 * For each row: Check if ANY column's value includes the search text
 * Filter condition: selectedTable.columnsNameList.some(col => 
 *   row[col]?.toString().toLowerCase().includes(searchText.toLowerCase())
 * )
 * This creates an OR logic (match any column) rather than AND logic (match all columns)
 */
export const AuditLog = () => {
  // ============================================================================
  // State Management - React Hooks
  // ============================================================================
  
  const [loading, setLoading] = useState(false);
  // Currently selected table object from API response
  const [selectedTable, setSelectedTable] = useState(null);
  
  // Raw data rows from API response - stored in state to persist between renders
  const [tableContent, setTableContent] = useState([]);
  
  // Filtered data after search (currently unused - DataTables handles filtering internally)
  const [filteredData, setFilteredData] = useState([]);
  
  // Current search query string - triggers DataTable recreation when changed
  const [searchText, setSearchText] = useState("");
  
  // Ref to actual table DOM element (prepared for potential future direct manipulation)
  const tableRef = useRef(null);


  /**
   * fetchData Function - Retrieves audit log data from server
   * 
   * Makes an asynchronous API call to get audit log entries and their column definitions.
   * Processes the response to extract the first available table and stores metadata and
   * data rows in React state. The loading state is managed to show/hide a spinner during
   * data retrieval.
   * 
   * API Call:
   * - Method: GET
   * - Endpoint: /reference/auditlog
   * - Response: { code: 200, data: Array<TableDefinition> }
   * 
   * Data Structure Processed:
   * Each table definition in response.data contains:
   * - tableName: Name of the table (string)
   * - columnsNameList: Array of column names to display in the table
   * - columnsType: Array of objects defining column types (select, text, etc.)
   * - data: Optional - actual row data if included in response
   * 
   * Error Handling:
   * - Response code check: Verifies result.code === 200
   * - Data validation: Checks if result.data array has at least one table
   * - Try-catch block: Catches network errors, parsing errors, API errors
   * - Console logging: Error messages logged but not displayed to user
   * 
   * @async
   * @function fetchData
   * @returns {Promise<void>}
   * 
   * @example
   * // Called on component mount via useEffect with empty dependencies
   * useEffect(() => { fetchData(); }, []);
   * 
   * Side Effects:
   * - Sets loading state to true (shows spinner)
   * - Updates selectedTable state with table metadata
   * - Updates tableContent state with data rows
   * - Sets loading state to false (hides spinner)
   */
  const fetchData = async () => {

    setLoading(true);
    try {
      // Make GET request to fetch audit log configuration and data
      let result = await apiCall("GET", `${API_SERVER_URL}reference/auditlog`);
      
      // Check if API returned success code and data contains at least one table
      if (result.code === 200 && result.data.length > 0) {

       /** 
        * Sample API Response Structure:
        * {
        *   "success": true,
        *   "code": 200,
        *   "data": [
        *     {
        *       "tableName": "Channels",
        *       "columnsNameList": [
        *         "Category",
        *         "ParentCategory",
        *         "CategoryType",
        *         "Synonyms",
        *         "Ignore"
        *       ],
        *       "columnsType": [
        *         {
        *           "Category": {
        *             "type": "select",
        *             "defaultValues": ["2-fer Knit Tops", "Ankle Sock", "value3"]  
        *           }
        *         },
        *         {
        *           "ParentCategory": {
                  "type":"select",
                  "defaultValues":["value1","value1","value1"]  
                },
                "CategoryType":{
                  "type":"text",
                  "defaultValues":[]  
                },
                "Synonyms":{
                  "type":"text",
                  "defaultValues":["test"]  
                },
                "Ignore":{
                  "type":"text",
                  "defaultValues":[]  
                }}
                ],
                  "columnsdata": [
                      {
                          "Category": "2-fer Knit Tops",
                          "ParentCategory": "Apparel",
                          "CategoryType": "ItemType",
                          "Synonyms": "",
                          "Ignore": false
                      },
                      {
                          "Category": "Ankle Sock",
                          "ParentCategory": "Hosiery",
                          "CategoryType": "ItemType",
                          "Synonyms": "Ankle Sock (Fuzzy Chenille)",
                          "Ignore": false
                      }
                  ]
              }
          ],
          "error": null,
          "code": 200
        }
        */

        /**
         * Data Processing Logic:
         * 1. Extract the first table definition from response array (tables may contain multiple audit tables)
         * 2. Store the actual data rows (columnsdata) in tableContent state
         * 3. Store the table metadata in selectedTable state with:
         *    - tableName: Display name for the table (e.g., "Channels")
         *    - columnsNameList: Array of column names to display in order
         *    - columnsType: Type definitions for each column (determines input type, default values, etc.)
         * 
         * This separation allows:
         * - selectedTable to provide configuration for the DataTable
         * - tableContent to provide the actual data rows to display
         * - DataTable to be destroyed and recreated when searchText changes
         */
        const tableData = result.data[0];
        // Store the actual data rows (array of objects where keys = column names)
        setTableContent(tableData.columnsdata);
        
        // Store table metadata for column definitions and configuration
        setSelectedTable({
          tableName: tableData.tableName,
          columnsNameList: tableData.columnsNameList,
          columnsType: tableData.columnsType
        });

      }
    } catch (error) {
      // Log any network, parsing, or API errors to browser console
      // In production, these could be sent to an error tracking service
      console.error("Error fetching table data:", error);
    }
    // Hide loading spinner after data fetch completes (success or error)
    setLoading(false);
  };

  /**
   * Initial Data Load Effect Hook
   * 
   * Runs once when component mounts (empty dependency array []).
   * Triggers the fetchData() function to retrieve audit log configuration and data.
   * 
   * Timing: Runs after component DOM is rendered but before user interactions
   * Cleanup: None needed - no return statement (no subscriptions/timers to clean up)
   * 
   * @requires fetchData - Must be defined above this effect
   */
  useEffect(() => {
    // Fetch audit log data on component mount
    fetchData();
}, []); // Empty dependency array = run only once on mount


/**
 * DataTable Initialization and Update Effect Hook
 * 
 * Dependencies: [tableContent, searchText]
 * Triggers whenever:
 * - tableContent array changes (new data from API)
 * - searchText changes (user types in search box)
 * 
 * Responsibility: Create/recreate jQuery DataTable with filtered data
 * 
 * Algorithm:
 * 1. Check if tableContent is not empty (table has data)
 * 2. Create DataTable configuration object
 * 3. Filter tableContent rows based on searchText before passing to DataTable
 * 4. Apply column configuration from selectedTable.columnsNameList
 * 5. Initialize jQuery DataTable with configuration
 * 6. Return cleanup function to destroy DataTable on unmount or next initialization
 * 
 * Cleanup: Destroys DataTable to prevent memory leaks when table is unmounted
 * 
 * Why destroy: true?
 * - Allows DataTable to be recreated when dependencies change
 * - jQuery DataTable plugins can conflict if not properly destroyed
 * - Multiple instances on same element would cause errors
 */
useEffect(() => {
  // Only initialize DataTable if table data is loaded
  if (tableContent.length > 0) {
    // Initialize jQuery DataTable with configuration
    let dataTable = $("#contract_grid").DataTable({
      // Destroy existing DataTable instance before creating new one
      // Necessary because we recreate when searchText changes
      destroy: true,
      
      // DOM Layout Configuration:
      // This controls which elements appear and their position
      // '<"top"f>' = search box in top container
      // 'rt' = table content with responsive wrapper
      // '<"bottom"ilp>' = info, length menu, pagination in bottom container
      "dom": '<"top"f>rt<"bottom"ilp><"clear">',
      
      // Horizontal Scrolling:
      // Enable horizontal scroll for tables wider than container
      scrollX: true,
      
      // Page Length Options:
      // First array: internal values for pagination
      // Second array: labels displayed to user (-1 = show all)
      "aLengthMenu": [[5,10,50,100,500, -1], [5,10,50,100,500,"All"]],
      
      // Pagination Style: simple_numbers shows 1, 2, 3... instead of Previous/Next
      "pagingType": "simple_numbers",
      
      // Default Page Size: Show 50 rows per page on initial load
      pageLength: 50,
      
      // Data Array: Filter the tableContent rows by search text BEFORE passing to DataTable
      // This implements client-side search filtering across all columns
      data: tableContent.filter(row =>
        // Check if ANY column in this row matches the search text
        selectedTable.columnsNameList.some(col =>
          // Convert cell value to lowercase string and check if it contains search text
          row[col]?.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      ).map((row) => {
        // Transform each row to only include columns that should be displayed
        // This filters out any extra data properties from the API response
        return selectedTable.columnsNameList.reduce((acc, columnKey) => {
          // Add each column value to the accumulator object
          // Use empty string as fallback if cell is null/undefined
          acc[columnKey] = row[columnKey] || "";
          return acc;
        }, {});
      }),
      
      // Column Configuration: Define columns dynamically from API response
      // Each column gets a title (column name) and data key (which property to read)
      columns: selectedTable.columnsNameList.map(col => ({ title: col, data: col })),
      
      // Initial Sort Order: Empty array means no automatic sorting
      // Users can click column headers to sort
      order: [],
    });
    
    // Cleanup Function: Destroy DataTable instance
    // Called when component unmounts or before recreating DataTable
    return () => {
      // Destroy jQuery DataTable instance to free resources
      dataTable.destroy();
    };
  }
}, [tableContent, searchText]);

// ============================================================================
// Render Section - JSX Layout
// ============================================================================

  return (
    // Main content wrapper with full-width container
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      {/* Page content section with padding and container */}
      <div className="page-content">
        {/* Bootstrap fluid container for full width with gutters */}
        <div className="container-fluid">
          {/* Header section with page title and description */}
          <div id="table_height">
            {/* Title and description flex container */}
            <div className="flex-grow-1" id="row_heading">
              {/* Page title: "Audit Log" */}
              <div className="page-title">Audit Log</div>
              {/* Descriptive subtitle explaining component purpose */}
              <p className="text-muted my-2 ff-secondary">
              Audit Log Data feature allows users to view modified data
              </p>
            </div>
            {/* Error message container (populated by JavaScript if needed) */}
            <div id="app_err"></div>
          </div>
          <br />
          
          {/* Conditional Rendering: Show loading spinner OR table content */}
          {loading ? <PageLoader /> : selectedTable !== null ? (
            <>
            {/* Search Bar and Action Buttons Row */}
            <div className="row g-2 mb-4 mt-4" id="row_search">
              {/* Search Input Column: 11/12 width */}
              <div className="col-sm-11">
                <div className="search-box">
                  {/* Search Input Field */}
                  <input
                    type="text"
                    className="form-control"
                    id="searchClausess"
                    placeholder="Search Taxonomies..."
                    // Update searchText state on every keystroke
                    // This triggers the DataTable useEffect to filter data
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {/* Search Icon: Lucide icon rendered via CustomIcon wrapper */}
                  <i className="grid-search">
                  <CommonIcon iconName="Search" size={"14px"}  />
                  </i>
                </div>
              </div>
              {/* Action Buttons Column: Right-aligned, auto width */}
              <div className="col-sm-auto ms-auto">
                <div className="list-grid-nav hstack gap-1">
                  {/* Refresh Button: Calls fetchData() to reload audit log */}
                  <button className="btn btn-light" onClick={fetchData}>
                  {/* RefreshCw icon from lucide-react via CustomIcon */}
                  <CommonIcon iconName="RefreshCw" size={"14px"} color={"#000"} />
                  </button>
                  {/* Commented Out: Add Taxonomy button (currently not needed) */}
                  {/* <button onClick={resetForm}
                    className="btn btn-primary uploadcontract-modal"
                    data-bs-toggle="modal"
                    data-bs-target="#addmemberModal"
                  >
                    <CommonIcon iconName="Plus" size={"14px"} color="#fff" /> Add Taxonomy
                  </button> */}
                </div>
              </div>
            </div> 
            
              {/* Table Container: Wrapper for horizontal scrolling */}
              <div className="table-container">
                {/* DataTable Element: jQuery DataTable will target this element via ID */}
                {/* id="contract_grid" is referenced in the useEffect DataTable initialization */}
                {/* Note: thead is empty - DataTable automatically generates headers from columns config */}
                <table
                      id="contract_grid"
                      className="display"
                      style={{ width: "100%" }}
                    >
                      {/* Empty thead: DataTable will populate with header row from columns config */}
                      <thead>
                      </thead>
                    </table>
              </div>
              <br />
            </>
          ) : ""}
        </div>
      </div>
    </div>
  );
};